/*******************************************************************************
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2015, 2024. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 *******************************************************************************/
'use strict';

var config = require('./config'),
  urls = require('./urls');
var AUTOMATION_CONTEXT_PATH = config.AUTOMATION_CONTEXT_PATH,
  CONTEXT_PATH = config.CONTEXT_PATH,
  DEVEX_PATH = config.DEVEX_PATH;
module.exports = {
  appDeploy: function appDeploy(appId, tenant) {
    return "".concat(DEVEX_PATH, "/").concat(encodeURIComponent(tenant), "/apps/").concat(encodeURIComponent(appId), "/deploy");
  },
  appDetails: function appDetails(appId, tenant) {
    return "".concat(DEVEX_PATH, "/").concat(encodeURIComponent(tenant), "/apps/").concat(encodeURIComponent(appId));
  },
  automation: function automation() {
    return "".concat(AUTOMATION_CONTEXT_PATH);
  },
  catalog: function catalog(toolchainId, envId) {
    var href = CONTEXT_PATH + '/catalog?toolchainId=' + encodeURIComponent(toolchainId);
    return addEnvId(href, envId);
  },
  catalogEntry: function catalogEntry(serviceId, toolchainId, app, envId) {
    var href = CONTEXT_PATH + '/catalog/' + encodeURIComponent(serviceId) + '?toolchainId=' + encodeURIComponent(toolchainId);
    if (app) {
      href += '&app_id=' + encodeURIComponent(app.id) + '&tenant=' + encodeURIComponent(app.tenant);
    }
    return addEnvId(href, envId);
  },
  cdCatalog: function cdCatalog(rgId, envId) {
    var href = '/catalog/services/continuous-delivery';
    href += rgId ? '?resource_group=' + rgId : '';
    return addLocation(href, envId);
  },
  clusterDetails: function clusterDetails(path, clusterId, region, resourceGroupId) {
    var parts = region.split(':'),
      shortendRegion = parts[parts.length - 1];
    return "/kubernetes/clusters/".concat(encodeURIComponent(clusterId), "/").concat(path, "?region=").concat(encodeURIComponent(shortendRegion), "&resourceGroup=").concat(encodeURIComponent(resourceGroupId));
  },
  clusters: function clusters() {
    return '/kubernetes/clusters';
  },
  configureService: function configureService(toolchainId, serviceInstanceId, envId) {
    var href = CONTEXT_PATH + '/toolchains/' + encodeURIComponent(toolchainId) + '/configure/' + encodeURIComponent(serviceInstanceId);
    return addEnvId(href, envId);
  },
  create: function create(repoUrl, branch, envId, zipUrl, templateType, app, contextParams) {
    var href = CONTEXT_PATH + '/setup/deploy?repository=' + encodeURIComponent(repoUrl);
    if (zipUrl) {
      href = CONTEXT_PATH + '/setup/deploy?zip_url=' + encodeURIComponent(zipUrl);
    }
    if (branch) {
      href += '&branch=' + encodeURIComponent(branch);
    }
    if (templateType) {
      switch (templateType) {
        case "github_repository":
          href += '&type=github';
        case "gitlab_repository":
          href += '&type=gitlab';
      }
    }
    if (app) {
      href += '&app_id=' + encodeURIComponent(app.id) + '&tenant=' + encodeURIComponent(app.tenant);
    }
    if (contextParams !== undefined) {
      href = addContextParams(href, contextParams);
    }
    return addEnvId(href, envId);
  },
  createCatalog: function createCatalog(envId, app, contextParams) {
    var href = CONTEXT_PATH + '/create';
    if (app) {
      href += '?app_id=' + encodeURIComponent(app.id) + '&tenant=' + encodeURIComponent(app.tenant);
      if (contextParams !== undefined) {
        href = addContextParams(href, contextParams);
      }
    }
    return addEnvId(href, envId);
  },
  createIksCatalog: function createIksCatalog(clusterName, clusterId, region, resourceGroupName, resourceGroupId) {
    return CONTEXT_PATH + "/create?fs-deploy-kube=true&k8s-cluster-name=".concat(encodeURIComponent(clusterName), "&k8s-cluster-id=").concat(encodeURIComponent(clusterId), "&k8s-region=").concat(encodeURIComponent(region), "&resource-group=").concat(encodeURIComponent(resourceGroupName), "&resource-group-id=").concat(encodeURIComponent(resourceGroupId));
  },
  createPermissions: function createPermissions(accountId, rgId, envId) {
    var href = "".concat(CONTEXT_PATH, "/accounts/").concat(encodeURIComponent(accountId), "/resource-groups/").concat(encodeURIComponent(rgId), "/permissions");
    href = urls.addUrlParam(href, 'create', 'true');
    return addEnvId(href, envId);
  },
  docUrl: function docUrl(path) {
    // currently does no processing, but serves as a centralized place to process for future, and identify all
    // doc urls
    return path;
  },
  gettingStarted: function gettingStarted(envId) {
    var href = CONTEXT_PATH + '/getting-started';
    return addEnvId(href, envId);
  },
  manageCdService: function manageCdService(serviceCrn, tab) {
    switch (tab) {
      case 'plan':
        return "/services/continuous-delivery/".concat(encodeURIComponent(serviceCrn), "?paneId=plan");
      case 'manage':
        return "/services/continuous-delivery/".concat(encodeURIComponent(serviceCrn), "?paneId=manage");
      default:
        return "/services/continuous-delivery/".concat(encodeURIComponent(serviceCrn));
    }
  },
  pipelineWorkerPools: function pipelineWorkerPools(envId) {
    var href = CONTEXT_PATH + '/pwp';
    return addEnvId(href, envId);
  },
  resourceList: function resourceList() {
    return '/resources';
  },
  toolchain: function toolchain(toolchainId, tab, envId) {
    var params = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    var href = CONTEXT_PATH + '/toolchains/' + encodeURIComponent(toolchainId);
    if (tab && tab !== 'tools') {
      // overview tab has no suffix
      href += '/' + encodeURIComponent(tab);
    }
    href = addEnvId(href, envId);
    // We could do this easily with a 'reduce', but this way we keep the order explicit.
    if (params['nav-mode'] === 'iks' && params['k8s-cluster-name'] && params['k8s-cluster-id'] && params['region'] && params['resource-group']) {
      href = "".concat(href).concat(href.includes('?') ? '&' : '?', "nav-mode=iks&k8s-cluster-name=").concat(encodeURIComponent(params['k8s-cluster-name']), "&k8s-cluster-id=").concat(encodeURIComponent(params['k8s-cluster-id']), "&region=").concat(encodeURIComponent(params['region']), "&resource-group=").concat(encodeURIComponent(params['resource-group']));
    }
    return href;
  },
  toolchains: function toolchains() {
    return "".concat(AUTOMATION_CONTEXT_PATH, "/toolchains");
  },
  iam: function iam(path) {
    return '/iam/' + path;
  }
};
function addEnvId(href, envId) {
  if (envId) {
    href = urls.addUrlParam(href, 'env_id', envId);
  }
  return href;
}
function addLocation(href, envId) {
  if (envId) {
    var location = envId.split(':')[2];
    href = urls.addUrlParam(href, 'location', location);
  }
  return href;
}
function addContextParams(href, contextParams) {
  if (href !== undefined && contextParams !== undefined) {
    // append the source_provider if specified...
    if (contextParams.source_provider !== undefined && contextParams.source_provider.length > 0) {
      href += '&source_provider=' + encodeURIComponent(contextParams.source_provider);
    }
    // append the app_repo if specified...
    if (contextParams.app_repo !== undefined && contextParams.app_repo.length > 0) {
      href += '&app_repo=' + encodeURIComponent(contextParams.app_repo);
    }
    // hoist user context params onto the url...
    // the following regex excludes list is derived from all of the reserved param names used across this
    // 'hrefs' class therefore anything else on the params that is not in this list is assumed as 'user context'...
    var EXCLUDE_PARAMS_TEMPLATE = /(repo[s\u017F]itory|ba[s\u017F]eName|env_id|zip_url|templateType|app_id|branch|tenant|toolchainId|[s\u017F]ource_provider|pipeline_type|app_repo|enableYmlPreproce[s\u017F][s\u017F]or|enableComplianceTemplate[s\u017F])/i;
    Object.keys(contextParams).forEach(function (param) {
      if (EXCLUDE_PARAMS_TEMPLATE.exec(param) === null) {
        href += "&" + encodeURIComponent(param) + "=" + encodeURIComponent(contextParams[param]);
      }
    });
  }
  return href;
}